import { getErrorMessage } from "@component/BabyLoan/SmallCalculator/domain";
import type { BabyLoanResponse } from "@component/BabyLoan/SmallCalculator/types";
import type { AbstractLeadFormProps, AbstractLeadFormState } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import { AbstractLeadForm } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import type {
	AbstractLeadCalculatorRequest,
	AbstractLeadCalculatorResponse,
	LeadServiceResponse,
	ProductType,
} from "@component/assets/AbstractLeadForm/types";
import { Config } from "@config";
import { formatText } from "@ui/format";
import { JsonDataBuilder } from "@utils/JsonDataBuilder";
import type { EventComponentType } from "@utils/google-analytics-4.type";
import { logError } from "@utils/sentry-error";
import { http } from "bm-ui-kit";
import type { CommonOptions } from "bm-ui-kit";
import CancelablePromise from "cancelable-promise";
import dayjs from "dayjs";
import type { ReactNode } from "react";
import React from "react";
import * as Step from "./Steps";
import { LOAN_GOALS } from "./Steps/Step2.data";
import type { LoanGoal } from "./Steps/Step2.data";
import {
	calculateBabyLoan,
	calculateCsok,
	calculateMortgage,
	getCsokSocialInsuranceSinceId,
	getTotalAmount,
} from "./domain";
import type { CsokResult, FamilyAndHousingAllowanceFormFields } from "./types";
import "./style.scss";

interface State extends AbstractLeadFormState<FamilyAndHousingAllowanceFormFields> {
	realEstateSizes: CommonOptions<number>;
	realEstateBuyings: CommonOptions<number>;
	csokResult: CsokResult;
	babyLoanResponse: BabyLoanResponse;
	mortgageLoanAmount: number;
}

/**
 * @Shortcode
 *
 * @title "Otthonteremtési kalkulátor"
 * @shortcode "family_and_housing_allowance_small"
 * @slug "csalad-es-lakastamogatas"
 * @type "calculator"
 */
export class FamilyAndHousingAllowanceSmallCalculator extends AbstractLeadForm<
	AbstractLeadCalculatorRequest,
	AbstractLeadCalculatorResponse,
	FamilyAndHousingAllowanceFormFields,
	AbstractLeadFormProps<AbstractLeadCalculatorRequest, AbstractLeadCalculatorResponse>,
	State
> {
	// EventParams
	protected readonly productTypes: string = "CSOK-TAMOGATAS,BABY_LOAN-TAMOGATAS,MORTGAGE-TAMOGATAS";
	protected readonly componentType: EventComponentType = "small-calculator";

	public static defaultProps = {
		title: "Kérj ingyenes segítséget szakértőnktől!",
		outsideTitle: "Otthonteremtési támogatások",
		bottomLogo: true,
	};

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			activeStep: "step-1",
			realEstateSizes: [],
			realEstateBuyings: [],
			csokResult: undefined,
			babyLoanResponse: undefined,
			mortgageLoanAmount: undefined,
		};
	}

	protected renderSteps(): ReactNode {
		const {
			values: { email, loanGoal, socialInsuranceSinceId },
			submitState,
			realEstateSizes,
			realEstateBuyings,
			csokResult,
			babyLoanResponse,
			mortgageLoanAmount,
		} = this.state;

		const hasBabyLoanOption = this.hasBabyLoanOptions();
		const totalAmount = getTotalAmount(csokResult);
		const hasLead = totalAmount || babyLoanResponse?.freelyUsableRealLoanAmount || mortgageLoanAmount;

		return (
			<>
				<Step.Step1 stepName="step-1" defaultTarget="step-2" />
				<Step.Step2 stepName="step-2" defaultTarget="step-3" submitState={submitState} canStepBack />
				<Step.Step3
					stepName="step-3"
					defaultTarget="step-4"
					realEstateSizes={realEstateSizes}
					realEstateBuyings={realEstateBuyings}
					loanGoal={loanGoal}
					submitState={submitState}
					canStepBack
				/>
				<Step.Step4
					stepName="step-4"
					defaultTarget="result-check-1"
					loanGoal={loanGoal}
					hasBabyLoanOption={hasBabyLoanOption}
					submitState={submitState}
					canStepBack
				/>
				<Step.Step5
					stepName="step-5"
					defaultTarget="step-6"
					hasEitherOfCoupleSocialInsurance={socialInsuranceSinceId >= 1}
					canStepBack
				/>
				<Step.Step6 stepName="step-6" defaultTarget="step-7" canStepBack />
				<Step.Step7 stepName="step-7" defaultTarget="result-check-2" canStepBack />
				<Step.Result
					stepName="result"
					defaultTarget="lead-form"
					availabelSupport={totalAmount}
					babyLoanAmount={babyLoanResponse?.freelyUsableRealLoanAmount}
					mortgageLoanAmount={mortgageLoanAmount}
					canStepBack
				/>
				<Step.ResultKo stepName="result-ko" defaultTarget="lead-form" canStepBack />
				<Step.LeadForm
					stepName="lead-form"
					defaultTarget="success-page"
					title="Add meg elérhetőséged és elküldjük a lakástámogatások részleteit e-mailben."
					button="Eredmény küldése"
					withCallIcon={!!mortgageLoanAmount || !!babyLoanResponse?.freelyUsableRealLoanAmount}
					withMortgage={!!mortgageLoanAmount}
					submitState={submitState}
					canStepBack
				/>
				<Step.SuccessPage
					stepName="success-page"
					title={hasLead ? undefined : "Köszönjük, hogy kalkuláltál!"}
					userEmail={email}
					subscriptionTypes={["RETAIL_NEWSLETTER"]}
				/>
			</>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		const {
			values: { otherRequiredProducts },
		} = this.state;

		switch (target) {
			case "step-3":
				this.onStep3(target);
				break;
			case "step-4":
				super.onStep(target, withEvent);
				break;
			case "step-7":
				super.onStep(target, withEvent);
				break;
			case "result-check-1":
				this.onResultCheck1();
				break;
			case "result-check-2":
				if (otherRequiredProducts.length > 0) {
					this.sendCalculationEvent();
				}
				this.onResultCheck2();
				break;
			case "success-page":
				this.onSuccessPage();
				break;
			default:
				super.onStep(target, withEvent);
		}
	}

	private onStep3(target: string): void {
		const {
			values,
			values: { loanGoal },
		} = this.state;
		const sendData = {
			...values,
			loanGoal: loanGoal === "TETOTER_BEEPITES" ? "EPITES" : loanGoal,
		};

		this.setState({ submitState: "loading" });

		const urlRealEstateSizes = Config.getApiUrl("/api/public/csok/real-estate-sizes", sendData);
		let urlRealEstateBuyings = Config.getApiUrl("/api/public/master-data/real-estate-buyings");

		if (sendData.loanGoal === "EPITES") {
			urlRealEstateBuyings = Config.getApiUrl("/api/public/master-data/real-estate-buildings");
		} else if (sendData.loanGoal === "BOVITES") {
			urlRealEstateBuyings = Config.getApiUrl("/api/public/master-data/real-estate-enlargements");
		}

		this.promisePool
			.add(Promise.all([http.getWithCache(urlRealEstateSizes), http.getWithCache(urlRealEstateBuyings)]))
			.then(([responseSizes, responseBuyings]) => {
				this.setState(
					{
						submitState: undefined,
						realEstateSizes: responseSizes.map((item) => ({
							value: item.id,
							label: item.name,
						})),
						realEstateBuyings: responseBuyings.map((item) => ({
							value: item.id,
							label: item.name,
						})),
					},
					() => {
						super.onStep(target, true);

						if (["result", "result-ko"].includes(target)) {
							this.sendViewItemListEvent(target);
						}
					},
				);
			})
			.catch((error: Error) => {
				this.setState({ submitState: "error" });

				this.promisePool.addTimeout().then(() => {
					this.setState({ submitState: undefined });
				});

				logError(error);
			});
	}

	private onResultCheck1(): void {
		const {
			values,
			values: { otherRequiredProducts },
		} = this.state;

		this.setState({ submitState: "loading" });

		this.promisePool
			.add(calculateCsok(values))
			.then((csokResult: CsokResult) => {
				let target = "result-ko";

				if (otherRequiredProducts.includes("BABY_LOAN")) {
					target = "step-5";
				} else if (otherRequiredProducts.includes("MORTGAGE")) {
					target = "step-6";
				} else if (csokResult.canGetCsok || csokResult.renovationAid || csokResult.vatRefund) {
					target = "result";
				}

				if (otherRequiredProducts.length === 0) {
					this.sendCalculationEvent();
				}

				this.setState(
					{
						submitState: undefined,
						csokResult,
					},
					() => {
						super.onStep(target, true);

						if (["result", "result-ko"].includes(target)) {
							this.sendViewItemListEvent(target);
						}
					},
				);
			})
			.catch((error: Error) => {
				this.setState({ submitState: "error" });

				this.promisePool.addTimeout().then(() => {
					this.setState({ submitState: undefined });
				});

				logError(error);
			});
	}

	private onResultCheck2(): void {
		const {
			values,
			values: { otherRequiredProducts },
			csokResult,
		} = this.state;

		this.setState({ submitState: "loading" });

		const babyLoanResponse = calculateBabyLoan(values);
		const babyLoanAmount = otherRequiredProducts.includes("BABY_LOAN")
			? babyLoanResponse?.freelyUsableRealLoanAmount
			: undefined;

		this.promisePool
			.add(calculateMortgage(values, babyLoanAmount, babyLoanResponse?.totalIncome))
			.then((mortgageLoanAmount: number) => {
				let target = "result-ko";

				if (
					csokResult.canGetCsok ||
					csokResult.renovationAid ||
					csokResult.vatRefund ||
					mortgageLoanAmount ||
					babyLoanAmount
				) {
					target = "result";
				}

				this.setState(
					{
						submitState: undefined,
						csokResult,
						babyLoanResponse: otherRequiredProducts.includes("BABY_LOAN") ? babyLoanResponse : undefined,
						mortgageLoanAmount,
					},
					() => {
						super.onStep(target, true);
					},
				);
			})
			.catch((error: Error) => {
				this.setState({ submitState: "error" });

				this.promisePool.addTimeout().then(() => {
					this.setState({ submitState: undefined });
				});

				logError(error);
			});
	}

	private onSuccessPage(): void {
		this.setState({
			submitState: "loading",
		});

		this.sendCsokLead().then(() => {
			this.sendMortgageLead().then(() => {
				this.promisePool.add(this.sendBabyLoanLead()).then(() => {
					const {
						values: { email },
					} = this.state;

					http.post(
						Config.getWpRestApiUrl(`/autoresponder/${encodeURIComponent(this.getAutoresponderName())}`),
						{
							email,
							fields: this.getAutoresponderFields(),
						},
					);

					super.onStep("success-page", false);

					this.setState({
						submitState: "done",
					});
				});
			});
		});
	}

	private sendCsokLead(): CancelablePromise<void | LeadServiceResponse> {
		const { csokResult } = this.state;

		if (csokResult.csokAmount) {
			return this.promisePool.add(this.sendProductSpecificLead("CSOK")).catch((error: Error) => {
				this.setState({ submitState: "error" });

				this.promisePool.addTimeout().then(() => {
					this.setState({ submitState: undefined });
				});

				logError(error);
			});
		}

		return CancelablePromise.resolve();
	}

	private sendMortgageLead(): CancelablePromise<void | LeadServiceResponse> {
		const { mortgageLoanAmount } = this.state;

		if (mortgageLoanAmount) {
			return this.promisePool.add(this.sendProductSpecificLead("MORTGAGE")).catch((error: Error) => {
				this.setState({ submitState: "error" });

				this.promisePool.addTimeout().then(() => {
					this.setState({ submitState: undefined });
				});

				logError(error);
			});
		}

		return CancelablePromise.resolve();
	}

	private sendBabyLoanLead(): CancelablePromise<void | LeadServiceResponse> {
		const { babyLoanResponse } = this.state;

		if (babyLoanResponse?.freelyUsableRealLoanAmount) {
			return this.promisePool.add(this.sendProductSpecificLead("BABY_LOAN")).catch((error: Error) => {
				this.setState({ submitState: "error" });

				this.promisePool.addTimeout().then(() => {
					this.setState({ submitState: undefined });
				});

				logError(error);
			});
		}

		return CancelablePromise.resolve();
	}

	private sendProductSpecificLead(productType: string): Promise<LeadServiceResponse> {
		const { ga4Event, response: responseProps } = this.props;
		const { values } = this.state;

		return http
			.post(Config.getWpRestApiUrl("/lead"), this.getProductSpecificLeadData(productType))
			.then((response: LeadServiceResponse) => {
				const leadId = response?.leadId > 0 ? response.leadId : undefined;

				if (leadId !== undefined) {
					this.sendEvent("BM_lead", {
						leadId,
						position: ga4Event?.position || 1,
						leadType: productType,
						...values,
						user_data: {
							email_address: values.email,
							phone_number: values.phone,
							address: {
								last_name: values.lastName,
								first_name: values.firstName,
								city: values.cityName,
							},
						},
						providerName: responseProps?.bankName,
						productName: responseProps?.productName,
						productId: responseProps?.id,
					});
				}

				return response;
			});
	}

	private sendViewItemListEvent(target: string): void {
		const { mortgageLoanAmount, babyLoanResponse, csokResult } = this.state;
		const totalAmount = getTotalAmount(csokResult);
		this.sendEvent("view_item_list", {
			item_list_id: this.calculationId,
			item_list_name: "normal",
			items: [
				{
					item_id: "",
					item_brand: "",
					item_name: "",
					affiliation: "product",
					index: 1,
					coupon: "none",
					currency: "HUF",
					discount: 0,
					item_category: target === "result-ko" ? "Sajnos nem vagy jogosult támogatás igénybevételére." : "", // KO üzenet szöveg
					item_category2: "",
					item_category3: "",
					item_category4: "",
					item_category5: "",
					item_variant: ``,
					price: totalAmount + (babyLoanResponse?.freelyUsableRealLoanAmount || 0), // támogatás + babaváró összege
					quantity: mortgageLoanAmount, // lakáshitel összege
				},
			],
		});
	}

	private hasBabyLoanOptions(): boolean {
		const {
			values: { married, taxDept, socialInsuranceSinceId },
		} = this.state;

		return married && !taxDept && socialInsuranceSinceId === 4;
	}

	private getProductSpecificLeadData(productType: string): Record<string, any> {
		const {
			values: {
				loanGoal,
				married,
				numberOfChildren,
				numberOfChildrenCommitment,
				taxDept,
				socialInsuranceSinceId,
				// ----
				realEstateValue,
				realEstateSizeId,
				ownRealEstateEnlargement,
				buyFromCloseRelative,
				earlierBoughtRealEstate,
				generalContractor,
				realEstateBuyings,
				plannedBudget,
				hasMoreThenHalfOwnership,
				// ---
				otherRequiredProducts,
				homeMeetsTheOneYearRequirement,
				lessThenFiveThousandsSettlement,
				// ---
				hasBothOfTheCoupleHunAddress,
				birthDate,
				isLast180daysSocInHun,
				loanAmount,
				incomeList,
				currentLoansMonthlyInstallment,
				creditLimitSumValue,
				inKHR,
				question,
				// ---
				cityId,
			},
			csokResult,
			babyLoanResponse,
			mortgageLoanAmount,
		} = this.state;

		const data: Record<string, any> = this.getLeadData();
		data.calculatorType = productType;
		data.productType = productType;
		data.productSubType = "TAMOGATAS";

		let productRequisitionTime = "";

		if (["BOVITES", "FELUJITAS", "KORSZERUSITES", "EPITES"].includes(loanGoal)) {
			switch (realEstateBuyings) {
				case 1:
					productRequisitionTime = "IMMEDIETLY";
					break;
				case 2:
					productRequisitionTime = "TWO_THREE_MONTHS";
					break;
				case 3:
					productRequisitionTime = "FOUR_SIX_MONTHS";
					break;
				case 4:
				case 5:
				default:
					productRequisitionTime = "TBD";
					break;
			}
		} else {
			switch (realEstateBuyings) {
				case 1:
					productRequisitionTime = "IMMEDIETLY";
					break;
				case 2:
					productRequisitionTime = "ONE_TWO_WEEKS";
					break;
				case 3:
					productRequisitionTime = "TWO_THREE_MONTHS";
					break;
				case 4:
					productRequisitionTime = "FOUR_SIX_MONTHS";
					break;
				case 5:
				default:
					productRequisitionTime = "TBD";
					break;
			}
		}

		const realEstateBuyingsName = this.state.realEstateBuyings.find(
			(item) => item.value === realEstateBuyings,
		)?.label;
		let fieldName = "realEstateBuying";
		switch (loanGoal) {
			case "EPITES":
			case "TETOTER_BEEPITES":
				fieldName = "realEstateBuilding";
				break;
			case "BOVITES":
				fieldName = "realEstateEnlargement";
				break;
			default:
		}

		if (productType === "CSOK") {
			data.request = {
				...data.request,
				loanGoal: loanGoal === "TETOTER_BEEPITES" ? "EPITES" : loanGoal,
				loanGoalName: LOAN_GOALS.find((item) => item.value === loanGoal)?.label,
				married,
				numberOfChildren,
				realEstateValue,
				inKHR,
				loanAmount,
				incomeList,
				numberOfChildrenCommitment,
				realEstateSize: realEstateSizeId,
				realEstateSizeName: this.state.realEstateSizes.find((item) => item.value === realEstateSizeId).label,
				taxDept,
				socialInsuranceSince: getCsokSocialInsuranceSinceId(socialInsuranceSinceId),
				// socialInsuranceSinceName, // TODO
				buyFromCloseRelative,
				generalContractor,
				ownRealEstateEnlargement,
				earlierBoughtRealEstate,
				needMortgageLoan: mortgageLoanAmount > 0,

				[fieldName]: `${realEstateBuyings}`,
				[`${fieldName}Name`]: `${realEstateBuyingsName}`,
			};

			data.response = {
				...data.response,
				csokAmount: csokResult.csokAmount,
				canGetCsok: csokResult.csokAmount > 0,
			};
		}

		if (productType === "BABY_LOAN") {
			data.request = {
				...data.request,
				incomeList,
				hasBothOfTheCoupleHunAddress,
				hasEitherOfCoupleNavDebt: taxDept,
				loanAmount,
				inKHR,
				married,
				birthDate,
				hasEitherOfCoupleSocialInsurance: socialInsuranceSinceId >= 1 ? "THREE_AND_MORE" : "LESS_THAN_THREE",
				isLast180daysSocInHun,
				currentLoansMonthlyInstallment,
				creditLimitSumValue,
			};

			data.response = {
				...data.response,
				extendBabyLoanWithCSOK: csokResult.csokAmount > 0,
				extendBabyLoanWithMortgage: mortgageLoanAmount > 0,
				extendBabyLoanWithPersonalLoan: false,
				ko0: false,
				ko1: false,
				ko2: false,
				ko3: false,
				ko4: false,
				ko5: false,
				ko6: false,
				ko7: false,
				ko8: false,
				hasKo: false,
				productName: "",
			};

			const otherData = new JsonDataBuilder(data.request.otherDataJson);
			otherData.addItem(
				"Mikor szeretnéd igényelni a Babaváró hitelt?",
				"productRequisitionTime",
				data.request.productRequisitionTime,
			);
			otherData.addItem("Úgy szeretnék hitelt felvenni, hogy KHR (BAR) listán vagyok?", "inKHR", inKHR, "bool");
			otherData.addItem("Havi törlesztő KIVÁLTÁS", "realInstallment", babyLoanResponse.realInstallment, "ft");
			otherData.addItem(
				"Max. vállalható törlesztő KIVÁLTÁS",
				"maxInstallment",
				babyLoanResponse.maxInstallment,
				"ft",
			);
			otherData.addItem("Hitelösszeg KIVÁLTÁS", "realLoanAmount", babyLoanResponse.realLoanAmount, "ft");
			otherData.addItem("Max. hitelösszeg KIVÁLTÁS ", "maxLoanAmount", babyLoanResponse.maxLoanAmount, "ft");

			otherData.addItem(
				"Havi törlesztő SZABFEL",
				"freelyUsableRealInstallment",
				babyLoanResponse.freelyUsableRealInstallment,
				"ft",
			);
			otherData.addItem(
				"Max. vállalható törlesztő SZABFEL",
				"maxFreelyUsableInstallment",
				babyLoanResponse.maxFreelyUsableInstallment,
				"ft",
			);
			otherData.addItem(
				"Hitelösszeg SZABFEL",
				"freelyUsableRealLoanAmount",
				babyLoanResponse.freelyUsableRealLoanAmount,
				"ft",
			);
			otherData.addItem(
				"Max. hitelösszeg SZABFEL",
				"maxFreelyUsableLoanAmount",
				babyLoanResponse.maxFreelyUsableLoanAmount,
				"ft",
			);

			otherData.addItem(
				"Van egy jövedelemből minimálbére?",
				"hasAcceptableIncome",
				babyLoanResponse.hasAcceptableIncome,
				"bool",
			);
			data.request.otherDataJson = otherData.getJson();

			data.wantsWithOnlineService = false;
		}

		if (productType === "MORTGAGE") {
			data.administrationLocation = cityId;
			data.request = {
				...data.request,
				loanGoalName: LOAN_GOALS.find((item) => item.value === loanGoal)?.label,
				loanGoalCode: loanGoal === "TETOTER_BEEPITES" ? "EPITES" : loanGoal,
				loanAmount: mortgageLoanAmount,
				realEstateValue,
				inKHR,
				incomeList,
				maturity: 20,
				age: dayjs().diff(birthDate, "year"),
				numberOfChildren,
				generalContractor,
			};

			data.response = {
				...data.response,
				productName: "",
			};
		}

		data.calculationData = JSON.stringify({
			loanGoal,
			married,
			numberOfChildren,
			numberOfChildrenCommitment,
			taxDept,
			socialInsuranceSinceId,
			// ----
			realEstateValue,
			realEstateSizeId,
			ownRealEstateEnlargement,
			buyFromCloseRelative,
			earlierBoughtRealEstate,
			generalContractor,
			plannedBudget,
			hasMoreThenHalfOwnership,
			// ---
			otherRequiredProducts,
			homeMeetsTheOneYearRequirement,
			lessThenFiveThousandsSettlement,
			// ---
			hasBothOfTheCoupleHunAddress,
			birthDate,
			isLast180daysSocInHun,
			loanAmount,
			incomeList,
			currentLoansMonthlyInstallment,
			creditLimitSumValue,
			inKHR,
			question,

			csokErrors: csokResult?.errorMessages,
			renovationErrors: csokResult?.renovationErrorMessages,
			babyLoanErrors: babyLoanResponse ? babyLoanResponse?.errors?.map((error) => getErrorMessage(error)) : [],

			[fieldName]: `${realEstateBuyings}`,
			[`${fieldName}Name`]: `${realEstateBuyingsName}`,
		});
		data.request.productRequisitionTime = productRequisitionTime;
		data.request.otherRequiredProducts = otherRequiredProducts.join(",");
		data.request.question = question;

		data.response.vatRefund = csokResult?.vatRefund || 0;
		data.response.feeDiscount = csokResult?.feeDiscount || 0;
		data.response.renovationAid = csokResult?.renovationAid || 0;
		data.response.babyLoanAmount = otherRequiredProducts.includes("BABY_LOAN")
			? babyLoanResponse?.freelyUsableRealLoanAmount
			: undefined;
		data.response.canGetMortgage = otherRequiredProducts.includes("MORTGAGE") ? mortgageLoanAmount > 0 : undefined;
		data.response.csokAmount = csokResult?.csokAmount || 0;
		data.response.relatedMortgageLoanAmount = data.response.canGetMortgage ? mortgageLoanAmount : undefined;

		return data;
	}

	protected getAutoresponderName(): string {
		return "autoresponder-housing-support";
	}

	protected getAutoresponderFields(): Record<string, any> {
		const {
			values: {
				firstName,
				lastName,
				realEstateValue,
				loanGoal,
				otherRequiredProducts,
				lessThenFiveThousandsSettlement,
				taxDept,
				generalContractor,
				socialInsuranceSinceId,
			},
			babyLoanResponse,
			csokResult,
			mortgageLoanAmount,
		} = this.state;

		const csokTotalAmount = getTotalAmount(csokResult);

		const canGetMorgage = !!mortgageLoanAmount;
		const mortgageOnly = !csokTotalAmount && !babyLoanResponse?.freelyUsableRealLoanAmount && canGetMorgage;
		const canGetAllowance = !!csokTotalAmount;
		const canGetLoan = !!babyLoanResponse?.freelyUsableRealLoanAmount || canGetMorgage;

		return {
			lastName,
			firstName,

			realEstateValue: formatText(realEstateValue, "ft"),
			loanGoal,
			loanGoalText: this.getLoanGoalText(loanGoal),
			lessThenFiveThousandsSettlement,

			needBabyLoan: otherRequiredProducts.includes("BABY_LOAN"),
			needMortgage: otherRequiredProducts.includes("MORTGAGE"),

			canGetCsok: csokResult?.canGetCsok,
			hasBabyLoanOption: this.hasBabyLoanOptions(),
			canGetAllowance,
			canGetLoan,
			mortgageOnly,

			csokAmount: csokResult?.csokAmount ? formatText(csokResult?.csokAmount, "ft") : undefined,
			feeDiscount: csokResult?.feeDiscount ? formatText(csokResult?.feeDiscount, "ft") : undefined,
			vatRefund: csokResult?.vatRefund ? formatText(csokResult?.vatRefund, "ft") : undefined,
			renovationAid: csokResult?.renovationAid ? formatText(csokResult?.renovationAid, "ft") : undefined,

			csokTotalAmount: csokTotalAmount ? formatText(csokTotalAmount, "ft") : undefined,
			mortgageLoanAmount:
				mortgageLoanAmount > 50_000
					? formatText(Math.floor(mortgageLoanAmount / 50_000) * 50_000, "ft")
					: undefined,
			babyLoanAmount: babyLoanResponse?.freelyUsableRealLoanAmount
				? formatText(babyLoanResponse.freelyUsableRealLoanAmount, "ft")
				: undefined,
			babyLoanMonthlyInstallment: babyLoanResponse?.freelyUsableRealLoanAmount
				? formatText(babyLoanResponse.freelyUsableRealInstallment, "ft")
				: undefined,

			csokErrors: csokResult?.errorMessages,
			renovationErrors: csokResult?.renovationErrorMessages,
			babyLoanErrors: babyLoanResponse
				? babyLoanResponse.errors.map((error) => getErrorMessage(error))
				: undefined,
			taxDept,
			generalContractor,
			socialInsuranceSinceId,
		};
	}

	private getLoanGoalText(loanGoal: LoanGoal): string {
		switch (loanGoal) {
			case "BOVITES":
				return "bővítenél";
			case "EPITES":
				return "építenél";
			case "TETOTER_BEEPITES":
				return "alakítanál ki tetőtér-beépítéssel";
			case "HASZNALT_LAKAS":
			case "UJ_LAKAS":
			default:
				return "vásárolnál";
		}
	}

	protected getProductType(): ProductType {
		return undefined; // NOTE: Ez nem valid
	}

	protected getClassName(): string {
		return `${super.getClassName()} family-and-housing-allowance`;
	}

	protected getComponentName(): string {
		return "FamilyAndHousingAllowanceSmallCalculator";
	}
}

export default FamilyAndHousingAllowanceSmallCalculator;
